import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  contactFieldsApi,
  fieldErrorsFrom,
  resolveVisibility,
  type ContactField,
  type FieldError,
  type FieldValue,
} from '../api/dynamicFields'
import { FormGrid, Button, Callout, Card, CardContent, CardHeader, Checkbox, Select, TextField } from './ui'
import { DynamicFileSelect } from './DynamicFileSelect'

/**
 * F-108 (TASK-0362, REQ-004-003): pannello "Campi aggiuntivi" della scheda cliente. Mostra i campi
 * configurati dall'Admin con destinazione Cliente (Impostazioni, Campi aggiuntivi), con i valori salvati.
 *
 * - Le condizioni di visualizzazione si valutano live (solo fra campi del cliente); il server le rivaluta.
 * - Il server rifiuta i valori non validi e gli obbligatori visibili rimasti vuoti (400, errore per campo).
 * - Sola lettura se il cliente e' nel cestino o fuso in un altro (`readOnly` del prop o della risposta).
 * - Nessun importo: i tipi Importo e Calcolato non esistono per il cliente.
 * - Senza campi configurati il pannello non occupa spazio.
 */
type EditValue = string | boolean | string[]

function fromServer(f: ContactField): EditValue {
  const v = f.value
  if (f.type === 'Casella') return v === true
  if (f.type === 'ElencoMultiplo') return Array.isArray(v) ? v : []
  if (v === null || v === undefined) return ''
  return String(v)
}

function isEmpty(v: EditValue | undefined): boolean {
  if (v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  return false
}

function toServer(f: Pick<ContactField, 'type'>, v: EditValue | undefined): FieldValue {
  if (f.type === 'Casella') return v === true
  if (isEmpty(v)) return null
  return v as string | string[]
}

export function ContactDynamicFields({
  contactId,
  readOnly = false,
  onSaved,
}: {
  contactId: number
  /** Sola lettura imposta dalla scheda (cliente nel cestino). */
  readOnly?: boolean
  onSaved?: () => void
}) {
  const [fields, setFields] = useState<ContactField[]>([])
  const [serverReadOnly, setServerReadOnly] = useState(false)
  const [readOnlyReason, setReadOnlyReason] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, EditValue>>({})
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [saved, setSaved] = useState(false)

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    contactFieldsApi
      .get(contactId)
      .then(r => {
        if (cancelled) return
        setFields(r.fields)
        setServerReadOnly(r.readOnly)
        setReadOnlyReason(r.readOnlyReason)
        const next: Record<string, EditValue> = {}
        for (const f of r.fields) next[f.code] = fromServer(f)
        setValues(next)
        setDirty(new Set())
        setFieldErrors([])
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento dei campi aggiuntivi')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [contactId])

  useEffect(() => load(), [load])

  const locked = readOnly || serverReadOnly

  const asFieldValues = useMemo(() => {
    const out: Record<string, FieldValue> = {}
    for (const f of fields) {
      const v = values[f.code]
      out[f.code] = f.type === 'Casella' ? v === true : isEmpty(v) ? null : (v as FieldValue)
    }
    return out
  }, [fields, values])

  const visibility = useMemo(() => resolveVisibility(fields, {}, asFieldValues), [fields, asFieldValues])

  function setValue(code: string, v: EditValue) {
    setValues(prev => ({ ...prev, [code]: v }))
    setDirty(prev => new Set(prev).add(code))
    setSaved(false)
  }

  async function save() {
    setSaving(true)
    setError(null)
    setFieldErrors([])
    try {
      const body: Record<string, FieldValue> = {}
      for (const f of fields) if (dirty.has(f.code)) body[f.code] = toServer(f, values[f.code])
      const r = await contactFieldsApi.save(contactId, body)
      setFields(r.fields)
      setServerReadOnly(r.readOnly)
      setReadOnlyReason(r.readOnlyReason)
      const next: Record<string, EditValue> = {}
      for (const f of r.fields) next[f.code] = fromServer(f)
      setValues(next)
      setDirty(new Set())
      setSaved(true)
      onSaved?.()
    } catch (e) {
      const list = fieldErrorsFrom(e)
      if (list.length > 0) setFieldErrors(list)
      else setError(e instanceof Error ? e.message : 'Salvataggio non riuscito')
    } finally {
      setSaving(false)
    }
  }

  const visibleFields = fields.filter(f => visibility[f.code] !== false)
  const sections = useMemo(() => {
    const map = new Map<string, ContactField[]>()
    for (const f of fields.filter(x => visibility[x.code] !== false)) {
      const key = f.section ?? 'Altri dati'
      map.set(key, [...(map.get(key) ?? []), f])
    }
    return [...map.entries()]
  }, [fields, visibility])

  if (loading) return <div className="text-muted md-typescale-body-small">Caricamento dei campi aggiuntivi...</div>
  // Nessun campo configurato per il cliente: non si occupa spazio.
  if (!error && visibleFields.length === 0) return null

  const errorOf = (code: string) => fieldErrors.find(e => e.code === code)?.message

  return (
    <Card variant="outlined" data-testid="contact-dynamic-fields">
      <CardHeader
        title="Campi aggiuntivi"
        actions={
          !locked && (
            <Button
              variant="filled"
              size="sm"
              disabled={saving || dirty.size === 0}
              onClick={() => void save()}
            >
              {saving ? 'Salvataggio...' : 'Salva i campi'}
            </Button>
          )
        }
      />
      <CardContent>
        {locked && (
          <Callout tone="info" role="status">
            Sola lettura{readOnlyReason ? `: ${readOnlyReason}` : '.'}
          </Callout>
        )}
        {error && <Callout tone="error">{error}</Callout>}
        {fieldErrors.length > 0 && (
          <Callout tone="error" role="alert">Correggi i campi evidenziati.</Callout>
        )}
        {saved && <Callout tone="success">Campi salvati.</Callout>}
        {sections.map(([section, list]) => (
          <div key={section} className="mb-3">
            {sections.length > 1 && <h3 className="md-typescale-title-small text-muted">{section}</h3>}
            <FormGrid columns={1}>
              {list.map(f => (
                <div key={f.code} className={f.type === 'ElencoMultiplo' || (f.type === 'Testo' && (f.validation?.maxLength ?? 0) > 200) ? 'md-span-full' : undefined}>
                  <ContactFieldEditor
                    field={f}
                    value={values[f.code]}
                    disabled={locked}
                    error={errorOf(f.code)}
                    onChange={v => setValue(f.code, v)}
                  />
                </div>
              ))}
            </FormGrid>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function ContactFieldEditor({
  field,
  value,
  disabled,
  error,
  onChange,
}: {
  field: ContactField
  value: EditValue | undefined
  disabled: boolean
  error: string | undefined
  onChange: (v: EditValue) => void
}) {
  const id = `cdf-${field.code}`
  const str = typeof value === 'string' ? value : ''
  const v = field.validation ?? {}
  const step = v.decimals !== undefined ? (v.decimals === 0 ? '1' : String(Math.pow(10, -v.decimals))) : 'any'
  const label = field.required ? `${field.label} *` : field.label

  if (field.type === 'Casella') {
    return (
      <div>
        <Checkbox
          id={id}
          label={label}
          checked={value === true}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          onChange={e => onChange(e.target.checked)}
        />
        {error && <div className="text-danger md-typescale-body-small">{error}</div>}
      </div>
    )
  }

  const common = { id, label, disabled, error }

  switch (field.type) {
    case 'Elenco':
      return (
        <Select {...common} value={str} onChange={e => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options.map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      )
    case 'ElencoMultiplo': {
      const picked = Array.isArray(value) ? value : []
      return (
        <div>
          <span id={`${id}-label`} className="md-typescale-label-large d-block mb-1">{label}</span>
          <div id={id} role="group" aria-labelledby={`${id}-label`}>
            {field.options.map(o => (
              <Checkbox
                key={o}
                id={`${id}-${o}`}
                label={o}
                disabled={disabled}
                checked={picked.includes(o)}
                onChange={e => onChange(e.target.checked ? [...picked, o] : picked.filter(x => x !== o))}
              />
            ))}
          </div>
          {error && <div className="text-danger md-typescale-body-small">{error}</div>}
        </div>
      )
    }
    case 'Numero':
    case 'Percentuale':
      return (
        <TextField
          {...common}
          type="number"
          value={str}
          step={step}
          {...(v.min !== undefined ? { min: v.min } : {})}
          {...(v.max !== undefined ? { max: v.max } : {})}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'Data':
      return <TextField {...common} type="date" value={str} onChange={e => onChange(e.target.value)} />
    case 'Email':
      return <TextField {...common} type="email" value={str} onChange={e => onChange(e.target.value)} />
    case 'Telefono':
      return <TextField {...common} type="tel" value={str} onChange={e => onChange(e.target.value)} />
    case 'CodiceFiscale':
      return (
        <TextField
          {...common}
          type="text"
          value={str}
          maxLength={16}
          uppercase
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'PartitaIva':
      return <TextField {...common} type="text" value={str} maxLength={13} onChange={e => onChange(e.target.value)} />
    case 'Iban':
      return (
        <TextField
          {...common}
          type="text"
          value={str}
          maxLength={42}
          uppercase
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'File':
      return <DynamicFileSelect {...common} fileRef={field.fileRef} value={str} disabled={disabled ?? false} onChange={onChange} />
    default:
      return (
        <TextField
          {...common}
          type="text"
          value={str}
          {...(v.maxLength !== undefined ? { maxLength: v.maxLength } : {})}
          onChange={e => onChange(e.target.value)}
        />
      )
  }
}
